import { DateTime } from "luxon";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import ApexCharts from "apexcharts";
import {
  getCountryName,
  countryISOMapping,
  getFictionalCountryByName,
  getCountryByName,
} from "../domain/countries";
import { getCompassDirection } from "../domain/geography";
import { useGuesses } from "../hooks/useGuesses";
import { CountryInput } from "./CountryInput";
import * as geolib from "geolib";
import { Share } from "./Share";
import { Guesses } from "./Guesses";
import { useTranslation } from "react-i18next";
import { SettingsData } from "../hooks/useSettings";
import { useMode } from "../hooks/useMode";
import { useCountry } from "../hooks/useCountry";
import axios from "axios";
import useConsentFromSearchParams from "../hooks/useConsentSearchParam";
import { useOECSession, type OECSession } from "../hooks/useOECSession";
import type { Guess } from "../domain/guess";
// import { BotMarketLink } from "./BotMarketLink";
import { OecLink } from "./OecLink";
import {
  APRIL_FOOLS_CELEBRATION,
  APRIL_FOOLS_PLACEHOLDER,
  APRIL_FOOLS_PROMPT,
  APRIL_FOOLS_TARGET,
  getAprilFoolsHint,
} from "../domain/aprilFools";

function getDayString() {
  // Parse query parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const dateParam = urlParams.get("date");

  // If date parameter exists, validate it
  if (dateParam) {
    // Try to parse the date using Luxon
    const parsedDate = DateTime.fromISO(dateParam);

    // Check if the date is valid and return it in the required format
    if (parsedDate.isValid) {
      return parsedDate.toFormat("yyyy-MM-dd");
    }
    // If invalid date parameter, we could show a toast notification here
    console.warn("Invalid date parameter, using current date instead");
  }

  // Fall back to current date if no valid date parameter
  return DateTime.now().toUTC().toFormat("yyyy-MM-dd");
}

const MAX_TRY_COUNT = 6;

interface GameProps {
  settingsData: SettingsData;
  updateSettings: (newSettings: Partial<SettingsData>) => void;
}

export function Game({ settingsData, updateSettings }: GameProps) {
  const { t, i18n } = useTranslation();
  const dayString = useMemo(getDayString, []);
  const isAprilFools = dayString.endsWith("04-01");

  // Add a check to see if we're using a historical date
  const isHistoricalPuzzle = useMemo(() => {
    const currentDayString = DateTime.now().toUTC().toFormat("yyyy-MM-dd");
    return dayString !== currentDayString;
  }, [dayString]);

  const countryInputRef = useRef<HTMLInputElement>(null);

  const consent = useConsentFromSearchParams();

  const session: OECSession = useOECSession();

  const countryData = useCountry(`${dayString}`);
  let country = countryData[0];
  console.log("Country", country);
  const countryCode = country ? country.code.toLowerCase() : "";
  fetch(`/country_data/exports/${countryCode}.json`)
    .then((res) => res.json())
    .then((countrySeries) => {
      console.log(countrySeries);
      const sum = Object.keys(countrySeries).reduce(
        (partialSum, a) => partialSum + countrySeries[a].y,
        0
      );
      const sum_str =
        sum > 1000000
          ? (sum / 1000000).toString().substring(0, 4) + "T$"
          : sum > 1000
          ? (sum / 1000).toString().substring(0, 4) + "B$"
          : sum.toString().substring(0, 4) + "M$";
      const chart_target = document.getElementById("chart_target");
      if (chart_target && !chart_target.hasChildNodes()) {
        const chart = new ApexCharts(chart_target, {
          series: [{ data: countrySeries }],
          chart: {
            type: "sunburst",
            height: 300,
          },
          colors: ["#0EA5E9", "#14B8A6", "#F59E0B"],
          legend: {
            show: false,
          },
          title: {
            text: sum_str,
            align: "center",
          },
          plotOptions: {
            sunburst: {
              innerSize: "25%",
              borderRadius: 5,
              spacing: 1,
            },
          },
          stroke: {
            width: 1,
            colors: ["#fff"],
          },
        });
        chart.render();
      }
    });

  if (isAprilFools) {
    country = APRIL_FOOLS_TARGET;
  }

  const [ipData, setIpData] = useState(null);
  const [currentGuess, setCurrentGuess] = useState<string>("");
  const [countryValue, setCountryValue] = useState<string>("");
  const [guesses, addGuess] = useGuesses(dayString);
  const [hideImageMode, setHideImageMode] = useMode(
    "hideImageMode",
    dayString,
    settingsData.noImageMode
  );
  const [rotationMode, setRotationMode] = useMode(
    "rotationMode",
    dayString,
    settingsData.rotationMode
  );

  const gameEnded =
    guesses.length === MAX_TRY_COUNT ||
    guesses[guesses.length - 1]?.distance === 0;
  const latestGuess = guesses[guesses.length - 1];
  const aprilFoolsHint = isAprilFools ? getAprilFoolsHint(latestGuess) : null;

  // Get IP data at the very begining
  useEffect(() => {
    const getIpData = async () => {
      const res = await axios.get("https://geolocation-db.com/json/");
      setIpData(res.data);
    };
    if (consent) {
      getIpData();
    }
  }, [consent]);

  // Callback for save score
  const saveScore = useCallback(
    (guesses, won) => {
      if (consent) {
        // console.log("saveScore!", consent, country, guesses, ipData, won);
        // Save score to db
        fetch("https://oec.world/api/games/score", {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            game: "tradle",
            meta: {
              user: ipData,
              ...(session ? { userId: session?.id } : {}),
            },
            answer: {
              country: country,
            },
            submission: { guesses },
            won,
          }),
        })
          // .then((dataRes) => console.log(dataRes))
          .catch((dataRes) =>
            console.error("Error saving tradle score", dataRes)
          );
      }
    },
    [consent, country, ipData, session]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!country) return;
      const guessedCountry = isAprilFools
        ? getFictionalCountryByName(currentGuess)
        : getCountryByName(currentGuess);

      if (guessedCountry == null) {
        toast.error(t("unknownCountry"));
        return;
      }

      const newGuess = {
        name: currentGuess,
        distance: geolib.getDistance(guessedCountry, country),
        direction: getCompassDirection(guessedCountry, country),
        country: guessedCountry,
      };

      const newGuesses: Guess[] = addGuess(newGuess);
      setCurrentGuess("");
      setCountryValue("");

      const gameEnded =
        newGuesses.length === MAX_TRY_COUNT ||
        newGuesses[newGuesses.length - 1]?.distance === 0;

      if (gameEnded) {
        const newWon = newGuess.distance === 0;
        saveScore(newGuesses, newWon);
      }
    },
    [addGuess, country, currentGuess, t, isAprilFools, saveScore]
  );

  useEffect(() => {
    if (
      guesses.length === MAX_TRY_COUNT &&
      guesses[guesses.length - 1].distance > 0
    ) {
      const countryName = country
        ? getCountryName(i18n.resolvedLanguage, country)
        : "";
      if (countryName) {
        toast.info(countryName.toUpperCase(), {
          autoClose: false,
          delay: 2000,
        });
      }
    }
  }, [country, guesses, i18n.resolvedLanguage]);

  const aprilFoolsBasePath = window.location.pathname.startsWith("/en/tradle")
    ? "/en/tradle"
    : "";
  const toggleDistanceUnit = useCallback(() => {
    updateSettings({
      distanceUnit: settingsData.distanceUnit === "km" ? "miles" : "km",
    });
  }, [settingsData.distanceUnit, updateSettings]);

  return (
    <div className="flex-grow flex flex-col mx-2 relative">
      {/* Add an indicator for historical puzzles */}
      {isHistoricalPuzzle && (
        <div
          className="mb-2 border-l-4 border-amber-500 bg-amber-100/90 p-2 text-amber-900 dark:bg-amber-500/10 dark:text-amber-100"
          role="alert"
        >
          <p className="text-center">
            You are playing a historical puzzle from {dayString}
          </p>
        </div>
      )}
      {hideImageMode && !gameEnded && (
        <button
          className="my-2 rounded-xl border border-slate-300/90 bg-white/70 px-4 py-2 uppercase text-slate-800 shadow-sm transition-colors hover:bg-white active:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:hover:bg-slate-900 dark:active:bg-slate-800"
          type="button"
          onClick={() => setHideImageMode(false)}
        >
          {t("showCountry")}
        </button>
      )}
      {/* <div className="my-1 mx-auto"> */}
      <OecLink />
      {/* <BotMarketLink /> */}
      <h2 className="mb-1 text-center font-bold text-slate-900 dark:text-slate-100">
        Hi{session ? `, ${session?.name || session?.email}` : ""}!{" "}
        {isAprilFools
          ? APRIL_FOOLS_PROMPT
          : "Guess which country exports these services!"}
      </h2>
      <div className="relative h-0 pt-[25px] pb-96 md:pb-[70%]">
        <div id="chart_target"></div>
      </div>
      {rotationMode && !hideImageMode && !gameEnded && (
        <button
          className="mb-2 rounded-xl border border-slate-300/90 bg-white/70 px-4 py-2 uppercase text-slate-800 shadow-sm transition-colors hover:bg-white active:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:hover:bg-slate-900 dark:active:bg-slate-800"
          type="button"
          onClick={() => setRotationMode(false)}
        >
          {t("cancelRotation")}
        </button>
      )}
      <Guesses
        rowCount={MAX_TRY_COUNT}
        guesses={guesses}
        settingsData={settingsData}
        toggleDistanceUnit={toggleDistanceUnit}
        countryInputRef={countryInputRef}
        isAprilFools={isAprilFools}
      />
      {aprilFoolsHint ? (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50/95 px-3 py-2 text-sm italic text-stone-700 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-100">
          {aprilFoolsHint}
        </div>
      ) : null}
      <div className="my-2">
        {gameEnded ? (
          <>
            <Share
              guesses={guesses}
              dayString={dayString}
              settingsData={settingsData}
              hideImageMode={hideImageMode}
              rotationMode={rotationMode}
              won={guesses[guesses.length - 1]?.distance === 0}
              isAprilFools={isAprilFools}
            />
            {!isAprilFools ? (
              <a
                className="underline w-full text-center block mt-4 flex justify-center"
                href=""
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                {t("showOnGoogleMaps")}
              </a>
            ) : null}
            {isAprilFools ? (
              <div className="w-full text-center block mt-4 flex flex-col justify-center text-2xl font-bold">
                <div>{APRIL_FOOLS_CELEBRATION.top}</div>
                <div>{APRIL_FOOLS_CELEBRATION.middle}</div>
                <div>{APRIL_FOOLS_CELEBRATION.bottom}</div>
              </div>
            ) : null}
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="">
              <CountryInput
                countryValue={countryValue}
                setCountryValue={setCountryValue}
                setCurrentGuess={setCurrentGuess}
                theme={settingsData.theme}
                isAprilFools={isAprilFools}
                guesses={guesses}
                placeholder={
                  isAprilFools ? APRIL_FOOLS_PLACEHOLDER : "Pick a location"
                }
              />
              {/* <button
                className="border-2 uppercase my-0.5 hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-slate-800 dark:active:bg-slate-700"
                type="submit"
              >
                🌍 {t("guess")}
              </button> */}
              <div className="text-left">
                <button className="my-2 inline-block items-center justify-end rounded-xl bg-slate-800 px-4 py-2 font-bold text-white shadow-sm transition-colors hover:bg-slate-900 dark:bg-oec-orange dark:text-slate-950 dark:hover:bg-orange-300">
                  {isAprilFools ? "🪱" : "🌍"} <span>Guess</span>
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
